import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Components } from 'react-markdown';

/**
 * Shared markdown-rendering layer for verse-page content strings.
 *
 * BUG THIS FIXES: content generated by the Sola insights API (both the
 * committed Tier 1 JSON in content/verse-pages/*.json and the live Tier 2
 * API responses assembled by verseContentBuilder.ts) comes back with inline
 * markdown syntax — `*entire*`, `**bold**`, embedded `### Subheading`s
 * inside commentarySections[].content, etc. Previously these strings were
 * dropped straight into JSX as plain text, so the raw asterisks/hashes
 * leaked into rendered prose. Every place that renders one of these strings
 * should go through RichText (multi-paragraph, e.g. commentary, FAQ
 * answers, narrativeContext) or InlineMarkdown (single-line/line-clamped
 * contexts, e.g. card excerpts, the keyInsight pull-quote) below, instead of
 * interpolating the string directly. Because both tiers share the same
 * VersePageContent shape and flow through the same components
 * (VerseArticle.tsx, the popular-verse cards on /bible-verse-explained),
 * fixing it here fixes it everywhere at once.
 *
 * `stripMarkdown` is for the handful of places that need a plain string
 * rather than JSX — <title>/<meta description> truncation and FAQPage
 * JSON-LD text, which should mirror the visible (now markdown-clean) copy.
 */

export function stripMarkdown(text: string): string {
  if (!text) return text;
  return text
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1') // images -> alt text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1') // links -> label
    .replace(/\*\*\*([^*]+)\*\*\*/g, '$1') // ***bold italic***
    .replace(/\*\*([^*]+)\*\*/g, '$1') // **bold**
    .replace(/\*([^*]+)\*/g, '$1') // *italic*
    .replace(/(?<![a-zA-Z0-9])_([^_]+)_(?![a-zA-Z0-9])/g, '$1') // _italic_
    .replace(/`([^`]+)`/g, '$1') // `code`
    .replace(/^#{1,6}\s+/gm, '') // # headings
    .replace(/\r\n/g, '\n')
    .trim();
}

// Block-level renderer — commentarySections content contains real paragraph
// breaks and occasional ### subheadings; narrativeContext/FAQ answers are
// usually single paragraphs. Headings are demoted to small tracked labels
// (never full <h2>/<h3> sizing) since they're annotations inside a section,
// not new document structure.
const blockComponents: Components = {
  p: ({ children }) => <p className="mb-4 last:mb-0">{children}</p>,
  h1: ({ children }) => (
    <p className="mt-6 mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-accent first:mt-0">{children}</p>
  ),
  h2: ({ children }) => (
    <p className="mt-6 mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-accent first:mt-0">{children}</p>
  ),
  h3: ({ children }) => (
    <p className="mt-6 mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-accent first:mt-0">{children}</p>
  ),
  h4: ({ children }) => (
    <p className="mt-5 mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-accent first:mt-0">{children}</p>
  ),
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="mb-4 ml-5 list-disc space-y-1.5">{children}</ul>,
  ol: ({ children }) => <ol className="mb-4 ml-5 list-decimal space-y-1.5">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  a: ({ href, children }) => (
    <a
      href={href}
      className="underline decoration-accent/50 underline-offset-2 hover:text-accent transition-colors"
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </a>
  ),
  blockquote: ({ children }) => <blockquote className="border-l-2 border-accent/40 pl-4 italic">{children}</blockquote>,
  code: ({ children }) => <code className="rounded bg-muted px-1 py-0.5 font-mono text-[0.9em]">{children}</code>,
  hr: () => <hr className="my-4 border-border" />,
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto">
      <table className="w-full text-sm border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => <thead>{children}</thead>,
  tbody: ({ children }) => <tbody>{children}</tbody>,
  tr: ({ children }) => <tr className="border-b border-border last:border-b-0">{children}</tr>,
  th: ({ children }) => (
    <th className="py-2 pr-4 text-left font-sans font-semibold text-foreground align-top">{children}</th>
  ),
  td: ({ children }) => <td className="py-2 pr-4 align-top">{children}</td>,
};

/** Multi-paragraph markdown content — commentary, narrativeContext, FAQ answers, citations. */
export function RichText({ text, className }: { text?: string | null; className?: string }) {
  if (!text) return null;
  return (
    <div className={className}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={blockComponents}>{text}</ReactMarkdown>
    </div>
  );
}

// Inline renderer — flattens the paragraph wrapper to a fragment so the
// result drops cleanly into a <span>/<p className="line-clamp-*"> without
// introducing a nested block element. Used for keyInsight pull-quotes and
// popular-verse card excerpts.
const inlineComponents: Components = {
  p: ({ children }) => <>{children}</>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ children }) => <>{children}</>,
  code: ({ children }) => <code className="font-mono text-[0.9em]">{children}</code>,
};

/** Single-line/line-clamped markdown content — no block elements in the output. */
export function InlineMarkdown({ text, className }: { text?: string | null; className?: string }) {
  if (!text) return null;
  return (
    <span className={className}>
      <ReactMarkdown components={inlineComponents}>{text}</ReactMarkdown>
    </span>
  );
}
